import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Settings as SettingsIcon, X, Plus, Bell, Filter } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { API_URL } from "@/config";
import { apiCall } from "@/utils/api";
import { cn, decodeHtmlEntities } from "@/lib/utils";
import { toast } from "sonner";

interface SystemSettings {
  assignation_enabled: boolean;
  last_assigned_index: number;
  email_notification_enabled: boolean;
  whatsapp_notification_enabled: boolean;
  excluded_tags: string[];
  virtual_class_defaults?: {
    default_max_enrollment_days?: number;
    formations?: Record<string, { max_enrollment_days: number }>;
  };
  assigned_courses: number[];
}

interface DurationInputProps {
  value: number; // in days
  onChange: (value: number) => void;
  className?: string;
  disabled?: boolean;
}

const DurationInput: React.FC<DurationInputProps> = ({ value, onChange, className, disabled }) => {
  // Logic: 1 Year = 365 days, 1 Month = 30 days
  const years = Math.floor(value / 365);
  const remainingDays = value % 365;
  const months = Math.floor(remainingDays / 30);
  const days = remainingDays % 30;

  const handleYearsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const y = parseInt(e.target.value) || 0;
    onChange((y * 365) + (months * 30) + days);
  };

  const handleMonthsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const m = parseInt(e.target.value) || 0;
    onChange((years * 365) + (m * 30) + days);
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const d = parseInt(e.target.value) || 0;
    onChange((years * 365) + (months * 30) + d);
  };

  // Generate arrays for options
  const yearOptions = Array.from({ length: 6 }, (_, i) => i); // 0 to 5
  const monthOptions = Array.from({ length: 13 }, (_, i) => i); // 0 to 12
  const dayOptions = Array.from({ length: 32 }, (_, i) => i); // 0 to 31

  const selectClassName = "h-12 bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-md focus:ring-primary/20 focus:border-primary block p-2.5 font-bold cursor-pointer hover:bg-slate-100 transition-colors appearance-none text-center min-w-[70px]";

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="relative">
        <select
          value={years}
          onChange={handleYearsChange}
          disabled={disabled}
          className={selectClassName}
        >
          {yearOptions.map((y) => (
            <option key={`y-${y}`} value={y}>
              {y}
            </option>
          ))}
        </select>
        <span className="absolute -bottom-4 left-0 w-full text-center text-[10px] font-bold text-slate-400 pointer-events-none">
          ANS
        </span>
      </div>

      <div className="relative">
        <select
          value={months}
          onChange={handleMonthsChange}
          disabled={disabled}
          className={selectClassName}
        >
          {monthOptions.map((m) => (
            <option key={`m-${m}`} value={m}>
              {m}
            </option>
          ))}
        </select>
        <span className="absolute -bottom-4 left-0 w-full text-center text-[10px] font-bold text-slate-400 pointer-events-none">
          MOIS
        </span>
      </div>

      <div className="relative">
        <select
          value={days}
          onChange={handleDaysChange}
          disabled={disabled}
          className={selectClassName}
        >
          {dayOptions.map((d) => (
            <option key={`d-${d}`} value={d}>
              {d}
            </option>
          ))}
        </select>
        <span className="absolute -bottom-4 left-0 w-full text-center text-[10px] font-bold text-slate-400 pointer-events-none">
          JOURS
        </span>
      </div>
    </div>
  );
};

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"automation" | "virtual_class">("automation");
  const [settings, setSettings] = useState<SystemSettings>({
    assignation_enabled: false,
    last_assigned_index: 0,
    email_notification_enabled: false,
    whatsapp_notification_enabled: false,
    excluded_tags: [],
    assigned_courses: []
  });

  const { token } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState<string>("");
  const [newTag, setNewTag] = useState<string>("");
  const [maxDays, setMaxDays] = useState<number>(0);

  // Récupérer les paramètres depuis le backend
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiCall<{
        success: boolean;
        settings: SystemSettings;
      }>(token, "/settings");
      setSettings(response.settings);
      if (response.settings.virtual_class_defaults?.default_max_enrollment_days !== undefined) {
        setMaxDays(response.settings.virtual_class_defaults.default_max_enrollment_days);
      }
    } catch (err) {
      setError("Failed to load settings");
      console.error("Error fetching settings:", err);
      toast.error("Erreur de chargement", {
        description: "Impossible de charger les paramètres"
      });
    } finally {
      setLoading(false);
    }
  };

  const [formations, setFormations] = useState<{ id: number; nom: string }[]>([]);
  const [formationSearch, setFormationSearch] = useState<string>("");

  // Fetch formations
  const fetchFormations = async () => {
    try {
      const response = await apiCall<{ id: number; nom: string }[]>(token, "/learnybox/courses");
      if (Array.isArray(response)) {
        setFormations(response);
      }
    } catch (err) {
      console.error("Failed to fetch formations:", err);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchFormations();
  }, []);

  const handleUpdateFormationDefault = async (courseId: number, days: number) => {
    try {
      setSaving(`formation_${courseId}`);
      // Only send the override, we need to merge with existing settings in backend OR fetch-modify-save
      // Since backend is simple update, we should replicate current state.
      const currentDefaults = settings.virtual_class_defaults || {};
      const currentFormations = currentDefaults.formations || {};

      const newFormations = {
        ...currentFormations,
        [courseId.toString()]: { max_enrollment_days: days }
      };

      if (days <= 0) {
        delete newFormations[courseId.toString()];
      }

      const newDefaults = {
        ...currentDefaults,
        formations: newFormations
      };

      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { defaults: any };
      }>(token, "/settings", "POST", {
        action: "update_virtual_class_defaults",
        defaults: newDefaults
      });

      setSettings((prev) => ({
        ...prev,
        virtual_class_defaults: response.data.defaults,
      }));

      toast.success("Paramètres mis à jour");
    } catch (err) {
      console.error(err);
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving("");
    }
  };

  const getFormationOverride = (courseId: number) => {
    return settings.virtual_class_defaults?.formations?.[courseId.toString()]?.max_enrollment_days || "";
  };


  // Activer/désactiver l'assignation automatique
  const handleToggleAssignment = async () => {
    try {
      setSaving("assignment");
      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { status: boolean };
      }>(token, "/settings", "POST", { action: "toggle_assignation" });
      setSettings((prev) => ({
        ...prev,
        assignation_enabled: response.data.status,
      }));

      toast.success(`${response.message}`);

    } catch (err) {
      setError("Failed to toggle assignment");
      console.error("Error toggling assignment:", err);
      toast.error("Erreur de configuration", {
        description: "Impossible de modifier l'assignation automatique"
      });
    } finally {
      setSaving("");
    }
  };

  // Réinitialiser l'index d'assignation
  const handleResetAssignment = async () => {
    try {
      setSaving("reset");
      const response = await apiCall<{
        success: boolean;
        data: { last_assigned_index: number };
      }>(token, "/settings", "POST", { action: "reset_assignment" });
      setSettings((prev) => ({
        ...prev,
        last_assigned_index: response.data.last_assigned_index,
      }));

      toast.success("Index d'assignation réinitialisé", {
        description: "L'index d'assignation a été remis à zéro"
      });
    } catch (err) {
      setError("Failed to reset assignment");
      console.error("Error resetting assignment:", err);
      toast.error("Erreur de réinitialisation", {
        description: "Impossible de réinitialiser l'index d'assignation"
      });
    } finally {
      setSaving("");
    }
  };

  // Activer/désactiver les notifications email
  const handleToggleEmailNotifications = async () => {
    try {
      setSaving("email");
      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { status: boolean };
      }>(token, "/settings", "POST", { action: "toggle_email_notification" });
      setSettings((prev) => ({
        ...prev,
        email_notification_enabled: response.data.status,
      }));

      toast.success(`${response.message}`);
    } catch (err) {
      setError("Failed to toggle email notifications");
      console.error("Error toggling email notifications:", err);
      toast.error("Erreur de notification", {
        description: "Impossible de modifier les notifications email"
      });
    } finally {
      setSaving("");
    }
  };

  // Activer/désactiver les notifications WhatsApp
  const handleToggleWhatsAppNotifications = async () => {
    try {
      setSaving("whatsapp");
      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { status: boolean };
      }>(token, "/settings", "POST", {
        action: "toggle_whatsapp_notification",
      });
      setSettings((prev) => ({
        ...prev,
        whatsapp_notification_enabled: response.data.status,
      }));

      toast.success(`${response.message}`);
    } catch (err) {
      setError("Failed to toggle WhatsApp notifications");
      console.error("Error toggling WhatsApp notifications:", err);
      toast.error("Erreur de notification", {
        description: "Impossible de modifier les notifications WhatsApp"
      });
    } finally {
      setSaving("");
    }
  };

  // Mettre à jour les tags exclus
  const handleUpdateExcludedTags = async (excludedTags: string[]) => {
    try {
      setSaving("excluded_tags");
      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { excluded_tags: string[] };
      }>(token, "/settings", "POST", {
        action: "update_excluded_tags",
        excluded_tags: excludedTags,
      });
      setSettings((prev) => ({
        ...prev,
        excluded_tags: response.data.excluded_tags,
      }));

      toast.success("Tags exclus mis à jour", {
        description: "La liste des tags exclus a été modifiée avec succès"
      });
    } catch (err) {
      setError("Failed to update excluded tags");
      console.error("Error updating excluded tags:", err);
      toast.error("Erreur de mise à jour", {
        description: "Impossible de mettre à jour les tags exclus"
      });
    } finally {
      setSaving("");
    }
  };

  // Ajouter un nouveau tag exclu
  const handleAddExcludedTag = () => {
    if (!newTag.trim()) return;

    const trimmedTag = newTag.trim();
    if (settings.excluded_tags.includes(trimmedTag)) {
      toast.error("Tag déjà présent", {
        description: "Ce tag est déjà dans la liste des tags exclus"
      });
      return;
    }

    const updatedTags = [...settings.excluded_tags, trimmedTag];
    handleUpdateExcludedTags(updatedTags);
    setNewTag("");
  };

  // Supprimer un tag exclu
  const handleRemoveExcludedTag = (tagToRemove: string) => {
    const updatedTags = settings.excluded_tags.filter(tag => tag !== tagToRemove);
    handleUpdateExcludedTags(updatedTags);
  };

  // Mettre à jour les paramètres par défaut des classes virtuelles
  const handleUpdateDefaults = async (days: number) => {
    try {
      setSaving("defaults");

      const currentDefaults = settings.virtual_class_defaults || {};
      const newDefaults = {
        ...currentDefaults,
        default_max_enrollment_days: days
      };

      const response = await apiCall<{
        success: boolean;
        message: string;
        data: { defaults: { default_max_enrollment_days: number } };
      }>(token, "/settings", "POST", {
        action: "update_virtual_class_defaults",
        defaults: newDefaults
      });

      setSettings((prev) => ({
        ...prev,
        virtual_class_defaults: response.data.defaults,
      }));

      toast.success("Paramètres mis à jour", {
        description: "Les filtres par défaut ont été enregistrés."
      });
    } catch (err) {
      setError("Failed to update defaults");
      console.error("Error updating defaults:", err);
      toast.error("Erreur de sauvegarde", {
        description: "Impossible de mettre à jour les paramètres."
      });
    } finally {
      setSaving("");
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const FormationItem = ({ formation }: { formation: { id: number; nom: string } }) => {
    const currentDays = parseInt(getFormationOverride(formation.id) as string || "0");

    return (
      <div className="flex items-center justify-between p-3 py-3 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex-1 min-w-0 mr-4">
          <div className="text-sm font-semibold text-slate-900 truncate" title={decodeHtmlEntities(formation.nom)}>{decodeHtmlEntities(formation.nom)}</div>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <DurationInput
            value={currentDays}
            onChange={(val) => {
              if (val !== currentDays) {
                handleUpdateFormationDefault(formation.id, val);
              }
            }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-4 space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
          <SettingsIcon className="h-32 w-32 rotate-12" />
        </div>
        <div className="z-10">
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
            Paramètres Système
          </h1>
          <p className="text-slate-500 font-medium mt-1">
            Configurez le comportement global de l'algorithme <span className="text-primary font-bold">AMC Dispatch</span>.
          </p>
        </div>
        <div className="z-10 flex gap-3">
          <div className="px-4 py-2 bg-slate-100 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            V2.1.0 CONFIG
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex p-1 bg-slate-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("automation")}
          className={cn(
            "px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200",
            activeTab === "automation"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
          )}
        >
          Automatisation
        </button>
        <button
          onClick={() => setActiveTab("virtual_class")}
          className={cn(
            "px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200",
            activeTab === "virtual_class"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
          )}
        >
          Classe Virtuelle
        </button>
      </div>

      <div className="grid gap-8 max-w-5xl">
        {activeTab === "automation" && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Assignment Settings */}
            <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white">
              <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                    <SettingsIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-slate-900">Configuration de l'Automatisme</CardTitle>
                    <CardDescription className="text-slate-500 font-medium">Contrôlez le flux de distribution et les notifications.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8 space-y-8">
                <div className="flex items-center justify-between p-6 bg-slate-50/50 rounded-2xl border border-slate-100 group hover:bg-white hover:shadow-sm transition-all duration-300">
                  <div className="space-y-1">
                    <Label className="text-base font-bold text-slate-900">Assignation automatique des leads</Label>
                    <p className="text-sm text-slate-500 font-medium">
                      Propulser les nouveaux leads vers les commerciaux disponibles en temps réel.
                    </p>
                  </div>
                  <Switch
                    checked={settings.assignation_enabled}
                    onCheckedChange={handleToggleAssignment}
                    disabled={saving === "assignment"}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-6 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-sm transition-all duration-300">
                    <div className="space-y-1">
                      <Label className="text-sm font-bold text-slate-900">Alertes Email</Label>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed">
                        Notification instantanée par email.
                      </p>
                    </div>
                    <Switch
                      checked={settings.email_notification_enabled}
                      onCheckedChange={handleToggleEmailNotifications}
                      disabled={saving === "email"}
                    />
                  </div>

                  <div className="flex items-center justify-between p-6 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-sm transition-all duration-300">
                    <div className="space-y-1">
                      <Label className="text-sm font-bold text-slate-900">Alertes WhatsApp</Label>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed">
                        Notification via l'API WhatsApp.
                      </p>
                    </div>
                    <Switch
                      checked={settings.whatsapp_notification_enabled}
                      onCheckedChange={handleToggleWhatsAppNotifications}
                      disabled={saving === "whatsapp"}
                    />
                  </div>
                </div>

                <div className="p-8 bg-indigo-50/30 rounded-3xl border border-indigo-100/50 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="space-y-2 text-center md:text-left">
                    <Label className="text-lg font-black text-slate-900">Séquenceur de Dispatch</Label>
                    <p className="text-sm text-slate-500 font-bold">
                      Dernière position assignée : <span className="text-indigo-600 bg-indigo-100/50 px-2 py-0.5 rounded-lg">Index {settings.last_assigned_index}</span>
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black">
                      Prochain tour : Utilisateur #{settings.last_assigned_index + 2}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleResetAssignment}
                    disabled={saving === "reset"}
                    className="bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-slate-600 font-bold rounded-xl h-12 px-6 shadow-sm border-slate-200 transition-all active:scale-95"
                  >
                    {saving === "reset"
                      ? "Réinitialisation..."
                      : "Réinitialiser la boucle"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Excluded Tags Settings */}
            <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white">
              <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                    <Bell className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-slate-900">Tags à Ignorer</CardTitle>
                    <CardDescription className="text-slate-500 font-medium">Les leads portant ces tags seront exclus du dispatch automatique.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    {settings.excluded_tags.length === 0 ? (
                      <div className="w-full py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <p className="text-slate-400 font-bold italic">
                          Aucune exception configurée.
                        </p>
                      </div>
                    ) : (
                      settings.excluded_tags.map((tag, index) => (
                        <div
                          key={index}
                          className="group flex items-center gap-2 bg-white border border-slate-200 pl-4 pr-2 py-2 rounded-xl text-sm font-bold text-slate-700 shadow-sm hover:border-primary/30 transition-all animate-in zoom-in-95"
                        >
                          <span>{tag}</span>
                          <button
                            onClick={() => handleRemoveExcludedTag(tag)}
                            disabled={saving === "excluded_tags"}
                            className="p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <Label className="text-sm font-bold text-slate-900 block mb-3">Ajouter une exclusion</Label>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder="Ex: TEST, DOUBLON, URGENT..."
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddExcludedTag();
                        }
                      }}
                      disabled={saving === "excluded_tags"}
                      className="flex-1 bg-slate-50/50 border-slate-200 rounded-xl h-12 font-medium focus:bg-white transition-all"
                    />
                    <Button
                      onClick={handleAddExcludedTag}
                      disabled={!newTag.trim() || saving === "excluded_tags"}
                      className="bg-primary hover:bg-primary/90 text-white font-bold rounded-xl px-6 h-12 shadow-lg shadow-primary/20 transition-all active:scale-95"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "virtual_class" && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Default Filters Settings */}
            <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white pt-0">
              <CardHeader className="bg-slate-50/50 border-b border-slate-100 px-8 pb-2 pt-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                    <Filter className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-slate-900">Filtres par Défaut</CardTitle>
                    <CardDescription className="text-slate-500 font-medium">Définissez les critères d'exclusion par défaut pour les formations.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8 space-y-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-2">
                    <Label htmlFor="default_days" className="text-base font-bold text-slate-900">Ancienneté maximum Globale</Label>
                    <p className="text-sm text-slate-500">
                      Les étudiants inscrits depuis plus de ce délai seront exclus par défaut.
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <DurationInput
                      value={maxDays}
                      onChange={(val) => setMaxDays(val)}
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdateDefaults(maxDays)}
                    disabled={saving === "defaults"}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {saving === "defaults" ? "Enregistrement..." : "Sauvegarder Global"}
                  </Button>
                </div>

                <div className="border-t border-slate-100 pt-6 mt-6">
                  <Label className="text-sm font-bold text-slate-900 block mb-4">Exceptions par Formation</Label>
                  <div className="space-y-3 overflow-y-auto pr-2">
                    <Input
                      placeholder="Rechercher une formation..."
                      value={formationSearch}
                      onChange={(e) => setFormationSearch(e.target.value)}
                      className="mb-4"
                    />

                    {formations
                      .filter(f => decodeHtmlEntities(f.nom).toLowerCase().includes(formationSearch.toLowerCase()))
                      .sort((a, b) => {
                        const hasOverrideA = getFormationOverride(a.id) !== "";
                        const hasOverrideB = getFormationOverride(b.id) !== "";
                        if (hasOverrideA && !hasOverrideB) return -1;
                        if (!hasOverrideA && hasOverrideB) return 1;
                        return a.nom.localeCompare(b.nom);
                      })
                      .map(formation => (
                        <FormationItem key={formation.id} formation={formation} />
                      ))}
                    {formations.length === 0 && (
                      <div className="text-center text-slate-400 py-4 text-sm">Chargement des formations...</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
